import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from '@dr.pogodin/react-helmet';
import { SEOHelmet, BreadcrumbList } from 'components/SEO';
import { ViewModeDropdown } from 'components/ViewModeDropdown';
import { useGetActivityStatsQuery } from 'store/services/activity/activityApi';
import { useActivitySocket } from 'hooks/useActivitySocket';
import { useFeed } from 'hooks/useFeed';
import LivePulseDashboard from 'screens/Main/tabs/Activity/components/LivePulseDashboard';
import FeedFilterBar from './components/FeedFilterBar';
import FeedPackagesView from './components/FeedPackagesView';
import FeedListView from './components/FeedListView';
import FeedNewWantsView from './components/FeedNewWantsView';
import FeedZeroState from './components/FeedZeroState';
import FeedNewItemsPill from './components/FeedNewItemsPill';
import type { FeedTrade, FeedWant } from 'types/interfaces/feed';
import {
  FeedViewMode,
  VIEW_MODE_STORAGE_KEY,
  VIEW_OPTIONS,
} from './constants';
import './FeedPage.css';

const getInitialViewMode = (): FeedViewMode => {
  const saved = typeof window !== 'undefined' ? window.localStorage.getItem(VIEW_MODE_STORAGE_KEY) : null;
  if (saved === 'packages' || saved === 'list') return saved;
  return 'packages';
};

const FeedPage: React.FC = () => {
  const [viewMode, setViewMode] = useState<FeedViewMode>(getInitialViewMode);

  const handleViewModeChange = (next: FeedViewMode) => {
    setViewMode(next);
    window.localStorage.setItem(VIEW_MODE_STORAGE_KEY, next);
  };

  const {
    filter,
    setFilter,
    cardSearch,
    setCardSearch,
    items,
    isLoading,
    hasMore,
    loadMore,
    isPaused,
    togglePause,
    pendingCount,
    flushPending,
    onScroll,
  } = useFeed();

  const { isConnected } = useActivitySocket({ enabled: true });

  const { data: stats, isLoading: statsLoading } = useGetActivityStatsQuery(undefined, {
    pollingInterval: isConnected ? 0 : 60000,
  });

  // Bind scroll to .main-page-content (used for both buffering detection + infinite scroll)
  const scrollContainerRef = useRef<Element | null>(null);
  useEffect(() => {
    const el = document.querySelector('.main-page-content');
    if (!el) return;
    scrollContainerRef.current = el;
    const handler = (e: Event) => {
      onScroll(e as unknown as React.UIEvent<HTMLElement>);

      const target = e.target as Element;
      const scrollTop = (target as HTMLElement).scrollTop ?? 0;
      const clientHeight = (target as HTMLElement).clientHeight ?? 0;
      const scrollHeight = (target as HTMLElement).scrollHeight ?? 0;
      if (scrollHeight - (scrollTop + clientHeight) < 400) {
        loadMore();
      }
    };
    el.addEventListener('scroll', handler, { passive: true });
    return () => el.removeEventListener('scroll', handler);
  }, [onScroll, loadMore]);

  const handleShowNew = () => {
    const el = scrollContainerRef.current;
    if (el) {
      (el as HTMLElement).scrollTo({ top: 0, behavior: 'smooth' });
    }
    flushPending();
  };

  const breadcrumbs = [
    { name: 'Home', url: 'https://trademagic.gg/home' },
    { name: 'Community Feed', url: 'https://trademagic.gg/feed' },
  ];

  const isWants = filter === 'new_wants';
  const isEmpty = !isLoading && items.length === 0;

  return (
    <>
      <SEOHelmet
        title="Community Feed - TradeMagic"
        description="Browse recent trades and want-list activity across the TradeMagic community."
        canonical="https://trademagic.gg/feed"
      />
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <BreadcrumbList breadcrumbs={breadcrumbs} />

      <div className="feed-page">
        <LivePulseDashboard
          stats={stats}
          isLoading={statsLoading}
          isConnected={isConnected}
        />

        <div className="feed-page__toolbar">
          <FeedFilterBar
            filter={filter}
            onFilterChange={setFilter}
            cardSearch={cardSearch}
            onCardSearchChange={setCardSearch}
            isPaused={isPaused}
            onTogglePause={togglePause}
            pendingCount={pendingCount}
          />
          {!isWants && (
            <ViewModeDropdown
              options={VIEW_OPTIONS}
              value={viewMode}
              onChange={handleViewModeChange}
            />
          )}
        </div>

        <FeedNewItemsPill
          count={pendingCount}
          onClick={handleShowNew}
          itemNoun={isWants ? 'wants' : 'trades'}
        />

        {isEmpty ? (
          <FeedZeroState />
        ) : isWants ? (
          <FeedNewWantsView wants={items as FeedWant[]} />
        ) : viewMode === 'list' ? (
          <FeedListView trades={items as FeedTrade[]} />
        ) : (
          <FeedPackagesView trades={items as FeedTrade[]} />
        )}

        {hasMore && !isEmpty && (
          <div className="feed-page__load-more">
            <button type="button" onClick={loadMore} className="feed-page__load-more-btn">
              Load more
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default FeedPage;
